import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import Breadcrumbs from "../../component/Breadcrumbs/Breadcrumbs";
import DefectLogs from "../../component/DefectManagement/DefectLogs";
import DetailCard from "../../component/DefectManagement/DetailCard";

/**
 * Reusable full-page activity / logs view.
 * Pass titles, breadcrumbs, timeline items, and optional header actions.
 */
const LogsPage = ({
  breadcrumbs = [],
  sectionLabel = "Logs",
  title = "—",
  subtitle = "",
  headerActions = null,
  timelineTitle = "Full Timeline",
  timelineDescription = "",
  items = [],
  emptyMessage = "No activity recorded.",
  onItemClick,
  isLoading = false,
  isError = false,
  errorMessage = "Unable to load logs.",
  backLabel = "Go back",
  onBack,
}) => {
  if (isLoading) {
    return (
      <Box className="flex min-h-[40vh] items-center justify-center p-6">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6">
        <Typography className="text-red-500">{errorMessage}</Typography>
        {onBack ? (
          <Typography
            className="cursor-pointer text-sm text-blue-600 hover:underline"
            onClick={onBack}
          >
            {backLabel}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <div className="pb-6">
      {breadcrumbs?.length ? (
        <div className="mx-3 mb-4 mt-3 sm:mx-5 md:mx-7">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      ) : null}

      <div className="mx-3 space-y-4 sm:mx-5 md:mx-7">
        <div className="rounded-[5px] border border-border-main bg-white px-5 py-4 shadow-lg">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box className="min-w-0">
              {sectionLabel ? (
                <p className="text-[14px] font-medium text-[#353F4F]">
                  {sectionLabel}
                </p>
              ) : null}
              <h3 className="mt-1 text-[20px] font-semibold text-[#232323] md:text-[24px]">
                {title || "—"}
              </h3>
              {subtitle ? (
                <p className="mt-1 text-[14px] text-[#353F4F]">{subtitle}</p>
              ) : null}
            </Box>

            {headerActions ? (
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                spacing={1.5}
                useFlexGap
              >
                {headerActions}
              </Stack>
            ) : null}
          </Stack>
        </div>

        <DetailCard title={timelineTitle}>
          {timelineDescription ? (
            <p className="mb-4 text-[14px] text-[#353F4F]">
              {timelineDescription}
            </p>
          ) : null}
          <DefectLogs
            items={items}
            variant="full"
            onItemClick={onItemClick}
            emptyMessage={emptyMessage}
          />
        </DetailCard>
      </div>
    </div>
  );
};

export default LogsPage;
